/**
 * Liveness for the shared graphql-ws WebSocket client (issue #2937).
 *
 * `keepAlive` makes graphql-ws send a Ping, but it never times out the Pong:
 * the next ping is armed only when a Pong arrives, and nothing happens if one
 * never does. So a socket that is dead but not closed is invisible to the
 * client — no `closed` event, no retry, and every subscriber sits on a
 * connection that will never deliver another message. That is the ordinary
 * outcome of a network change: Chrome fails in-flight requests with
 * `ERR_NETWORK_CHANGED` and leaves the WebSocket half-open.
 *
 * This module supplies the missing deadline, plus the two things the browser's
 * own network signal is good for: cutting a reconnect backoff short when the
 * network returns, and replacing a socket that a network transition has
 * certainly broken even though the socket has not noticed.
 *
 * It is deliberately separate from `client.ts` so it can be tested with fake
 * timers without standing up an Apollo client or opening a real WebSocket.
 */

export interface WsLivenessOptions {
  /** How long a Ping we sent may go unanswered before the socket is dead. */
  pongTimeoutMs: number;
  /** Drop the current socket so the retry loop re-establishes it. */
  terminate: () => void;
  /** Optional diagnostics sink. */
  log?: (message: string) => void;
}

export interface WsLiveness {
  /** graphql-ws `on.ping`. `receivedFromServer` is its first argument. */
  handlePing(receivedFromServer: boolean): void;
  /** graphql-ws `on.pong`. `receivedFromServer` is its first argument. */
  handlePong(receivedFromServer: boolean): void;
  /** A socket now exists: graphql-ws `on.connecting` and `on.connected`. */
  handleSocketPresent(): void;
  /** No socket exists: graphql-ws `on.closed` and `on.error`. */
  handleSocketGone(): void;
  /** graphql-ws `retryWait` body: waits `delayMs`, or until the network returns. */
  waitForBackoff(delayMs: number): Promise<void>;
  /**
   * Prod the transport from outside. Releases any reconnect backoff so the next
   * handshake happens now instead of at the end of a 45s wait, and — when the
   * caller knows the current socket cannot have survived whatever just happened
   * — replaces it. The `online` listener below is one caller; the subscription
   * supervisor is the other, on a tab becoming visible again.
   */
  nudge(options?: { replaceSocket?: boolean }): void;
  /** Detach the window listener and release anything pending. */
  dispose(): void;
}

export function createWsLiveness(options: WsLivenessOptions): WsLiveness {
  const { pongTimeoutMs, terminate, log } = options;

  let pongTimer: ReturnType<typeof setTimeout> | null = null;
  // Whether a socket exists at all — handshaking or established. Tracked here
  // rather than read off the client's connection state, because that state
  // cannot distinguish a retry handshake in flight from the backoff wait that
  // precedes it: graphql-ws reports both as "reconnecting". The distinction
  // matters in both directions — see `nudge`.
  let socketPresent = false;
  const backoffWaiters = new Set<() => void>();

  function clearPongTimer(): void {
    if (pongTimer !== null) {
      clearTimeout(pongTimer);
      pongTimer = null;
    }
  }

  function handlePing(receivedFromServer: boolean): void {
    // A Ping *from* the server is answered by graphql-ws itself and says
    // nothing about whether our own frames are getting out. Only a Ping we
    // sent is a probe worth putting a deadline on.
    if (receivedFromServer) return;
    if (pongTimer !== null) return;

    pongTimer = setTimeout(() => {
      pongTimer = null;
      log?.("Pong not received within the deadline; terminating the stale socket");
      terminate();
    }, pongTimeoutMs);
  }

  function handlePong(receivedFromServer: boolean): void {
    // `on.pong` also fires for the Pong we send in reply to a server Ping
    // (`receivedFromServer === false`). That is our own outbound frame and
    // proves nothing about the return path, which is what the deadline tests.
    if (!receivedFromServer) return;
    clearPongTimer();
  }

  function handleSocketPresent(): void {
    // A replacement socket must not inherit the previous one's deadline, or
    // the inherited timer fires against a healthy connection and kills it.
    clearPongTimer();
    socketPresent = true;
  }

  function handleSocketGone(): void {
    clearPongTimer();
    socketPresent = false;
  }

  function releaseBackoffWaiters(): void {
    const waiters = Array.from(backoffWaiters);
    backoffWaiters.clear();
    waiters.forEach((release) => release());
  }

  function nudge(options: { replaceSocket?: boolean } = {}): void {
    // Stop waiting out the rest of the backoff: whatever prompted the nudge is
    // new information, and the remaining wait was chosen without it.
    releaseBackoffWaiters();

    // A socket that predates the event may be broken whether or not it has
    // noticed. Only the caller knows: `online` fires on a transition out of
    // offline, so it can say so; a tab merely becoming visible cannot.
    //
    // Replacing covers the handshake as well as the established connection, and
    // the handshake is the case with no other detector: the pong deadline is not
    // armed until the socket opens, and the backoff wait is not re-entered
    // until the attempt errors or closes, which a half-open handshake does not
    // do until the browser's own connect timeout expires — minutes away. An
    // established socket would at least be caught by the next Ping.
    if (options.replaceSocket && socketPresent) {
      clearPongTimer();
      log?.("Replacing a socket that cannot have survived the last transition");
      terminate();
    }
  }

  function handleOnline(): void {
    nudge({ replaceSocket: true });
  }

  function waitForBackoff(delayMs: number): Promise<void> {
    return new Promise<void>((resolve) => {
      let timer: ReturnType<typeof setTimeout> | null = null;

      const release = () => {
        if (timer !== null) {
          clearTimeout(timer);
          timer = null;
        }
        backoffWaiters.delete(release);
        resolve();
      };

      backoffWaiters.add(release);
      timer = setTimeout(release, delayMs);
    });
  }

  if (typeof window !== "undefined") {
    window.addEventListener("online", handleOnline);
  }

  function dispose(): void {
    clearPongTimer();
    releaseBackoffWaiters();
    socketPresent = false;
    if (typeof window !== "undefined") {
      window.removeEventListener("online", handleOnline);
    }
  }

  return { handlePing, handlePong, handleSocketPresent, handleSocketGone, waitForBackoff, nudge, dispose };
}
